import PropTypes from 'prop-types';
import React from 'react';
import {View} from 'react-native';
import _ from 'underscore';
import getStyledTextArray from '@libs/GetStyledTextArray';
import * as StyleUtils from '@styles/StyleUtils';
import useTheme from '@styles/themes/useTheme';
import useThemeStyles from '@styles/useThemeStyles';
import CONST from '@src/CONST';
import AutoCompleteSuggestions from './AutoCompleteSuggestions';
import Avatar from './Avatar';
import avatarPropTypes from './avatarPropTypes';
import Text from './Text';

const propTypes = {
    /** The index of the highlighted mention */
    highlightedMentionIndex: PropTypes.number,

    /** Array of suggested mentions */
    mentions: PropTypes.arrayOf(
        PropTypes.shape({
            /** Display name of the user */
            text: PropTypes.string,

            /** Email/phone number of the user */
            alternateText: PropTypes.string,

            /** Array of icons of the user. We use the first element of this array */
            icons: PropTypes.arrayOf(avatarPropTypes),
        }),
    ).isRequired,

    /** Fired when the user selects an mention */
    onSelect: PropTypes.func.isRequired,

    /** Mention prefix that follows the @ sign  */
    prefix: PropTypes.string.isRequired,

    /** Show that we can use large mention picker.
     * Depending on available space and whether the input is expanded, we can have a small or large mention suggester.
     * When this value is false, the suggester will have a height of 2.5 items. When this value is true, the height can be up to 5 items.  */
    isMentionPickerLarge: PropTypes.bool.isRequired,

    /** Meaures the parent container's position and dimensions. */
    measureParentContainer: PropTypes.func,
};

const defaultProps = {
    highlightedMentionIndex: 0,
    measureParentContainer: () => {},
};

/**
 * Create unique keys for each mention item
 * @param {Object} item
 * @param {Number} index
 * @returns {String}
 */
const keyExtractor = (item) => item.alternateText;

function MentionSuggestions(props) {
    const theme = useTheme();
    const styles = useThemeStyles();
    /**
     * Render a suggestion menu item component.
     * @param {Object} item
     * @returns {JSX.Element}
     */
    const renderSuggestionMenuItem = (item) => {
        const isIcon = item.text === CONST.AUTO_COMPLETE_SUGGESTER.HERE_TEXT;
        const styledDisplayName = getStyledTextArray(item.text, props.prefix);
        const styledHandle = item.text === item.alternateText ? '' : getStyledTextArray(item.alternateText, props.prefix);

        return (
            <View style={[styles.autoCompleteSuggestionContainer, styles.ph2]}>
                <View style={styles.mentionSuggestionsAvatarContainer}>
                    <Avatar
                        source={item.icons[0].source}
                        size={isIcon ? CONST.AVATAR_SIZE.MENTION_ICON : CONST.AVATAR_SIZE.SMALLER}
                        name={item.icons[0].name}
                        type={item.icons[0].type}
                        fill={theme.success}
                        fallbackIcon={item.icons[0].fallbackIcon}
                    />
                </View>
                <Text
                    style={[styles.mentionSuggestionsText, styles.flexShrink1]}
                    numberOfLines={1}
                >
                    {_.map(styledDisplayName, ({text, isColored}, i) => (
                        <Text
                            key={`${text}${i}`}
                            style={[StyleUtils.getColoredBackgroundStyle(isColored), styles.mentionSuggestionsDisplayName]}
                        >
                            {text}
                        </Text>
                    ))}
                </Text>
                <Text
                    style={[styles.mentionSuggestionsText, styles.flex1]}
                    numberOfLines={1}
                >
                    {_.map(
                        styledHandle,
                        ({text, isColored}, i) =>
                            text !== '' && (
                                <Text
                                    key={`${text}${i}`}
                                    style={[StyleUtils.getColoredBackgroundStyle(isColored), styles.mentionSuggestionsHandle, {...(isColored && {color: styles.text})}]}
                                >
                                    {text}
                                </Text>
                            ),
                    )}
                </Text>
            </View>
        );
    };

    return (
        <AutoCompleteSuggestions
            suggestions={props.mentions}
            renderSuggestionMenuItem={renderSuggestionMenuItem}
            keyExtractor={keyExtractor}
            highlightedSuggestionIndex={props.highlightedMentionIndex}
            onSelect={props.onSelect}
            isSuggestionPickerLarge={props.isMentionPickerLarge}
            accessibilityLabelExtractor={keyExtractor}
            measureParentContainer={props.measureParentContainer}
        />
    );
}

MentionSuggestions.propTypes = propTypes;
MentionSuggestions.defaultProps = defaultProps;
MentionSuggestions.displayName = 'MentionSuggestions';

export default MentionSuggestions;
